import { create } from 'zustand';
import { devtools } from 'zustand/middleware';

/**
 * UI Store - Domain-focused state management for UI state and loading indicators
 * 
 * Responsibilities:
 * - Global loading states
 * - UI interaction states
 * - Modal and drawer states
 * - Toast and notification states
 * 
 * Following industry best practices:
 * - Single Responsibility Principle
 * - Immutable state updates
 * - Separation of concerns
 * - Type safety
 */

// ===== INTERFACES =====

interface LoadingState {
  isLoading: boolean;
  loadingMessage?: string;
  progress?: number; // 0-100 for progress indicators
}

interface ModalState {
  isOpen: boolean;
  modalType?: string;
  modalData?: any;
}

interface DrawerState {
  isOpen: boolean;
  drawerType?: string;
  drawerHeight?: string;
  drawerData?: any;
}

interface NotificationState {
  notifications: Notification[];
  maxNotifications: number;
}

interface Notification {
  id: string;
  type: 'info' | 'success' | 'warning' | 'error';
  title: string;
  message?: string;
  duration?: number; // ms, 0 for persistent
  timestamp: number;
}

interface UIState {
  // Global loading state
  globalLoading: LoadingState;
  
  // Specific loading states
  loadingStates: Record<string, LoadingState>;
  
  // Modal state
  modal: ModalState;
  
  // Drawer state
  drawer: DrawerState;
  
  // Notification state
  notifications: NotificationState;
  
  // General UI state
  isInitialized: boolean;
  theme: 'light' | 'dark' | 'auto';
  isMobile: boolean;
  isOnline: boolean;
  
  // Error boundary state
  hasGlobalError: boolean;
  globalError: string | null;
}

interface UIActions {
  // Global loading operations
  setGlobalLoading: (isLoading: boolean, message?: string, progress?: number) => void;
  clearGlobalLoading: () => void;
  
  // Specific loading operations
  setLoading: (key: string, isLoading: boolean, message?: string, progress?: number) => void;
  clearLoading: (key: string) => void;
  clearAllLoading: () => void;
  
  // Modal operations
  openModal: (modalType: string, data?: any) => void;
  closeModal: () => void;
  
  // Drawer operations
  openDrawer: (drawerType: string, data?: any, height?: string) => void;
  closeDrawer: () => void;
  setDrawerHeight: (height: string) => void;
  
  // Notification operations
  addNotification: (notification: Omit<Notification, 'id' | 'timestamp'>) => void;
  removeNotification: (id: string) => void;
  clearNotifications: () => void;
  
  // General UI operations
  setInitialized: (isInitialized: boolean) => void;
  setTheme: (theme: 'light' | 'dark' | 'auto') => void;
  setMobile: (isMobile: boolean) => void;
  setOnline: (isOnline: boolean) => void;
  
  // Error boundary operations
  setGlobalError: (error: string | null) => void;
  clearGlobalError: () => void;
  
  // Utility functions
  isAnyLoading: () => boolean;
  getLoadingKeys: () => string[];
}

type UIStore = UIState & UIActions;

// ===== INITIAL STATE =====

const initialGlobalLoading: LoadingState = {
  isLoading: false,
  loadingMessage: undefined,
  progress: undefined,
};

const initialModal: ModalState = {
  isOpen: false,
  modalType: undefined,
  modalData: undefined,
};

const initialDrawer: DrawerState = {
  isOpen: false,
  drawerType: undefined,
  drawerHeight: '50%',
  drawerData: undefined,
};

const initialNotifications: NotificationState = {
  notifications: [],
  maxNotifications: 5,
};

const initialState: UIState = {
  globalLoading: initialGlobalLoading,
  loadingStates: {},
  modal: initialModal,
  drawer: initialDrawer,
  notifications: initialNotifications,
  isInitialized: false,
  theme: 'auto',
  isMobile: false,
  isOnline: true,
  hasGlobalError: false,
  globalError: null,
};

// ===== UTILITIES =====

/**
 * Generate a unique ID for notifications
 */
const generateId = (): string => {
  return `${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
};

// ===== STORE IMPLEMENTATION =====

export const useUIStore = create<UIStore>()(
  devtools(
    (set, get) => ({
      ...initialState,

      // ===== GLOBAL LOADING OPERATIONS =====
      
      setGlobalLoading: (isLoading: boolean, message?: string, progress?: number) => {
        set({
          globalLoading: {
            isLoading,
            loadingMessage: message,
            progress,
          }
        }, false, 'ui/setGlobalLoading');
      },

      clearGlobalLoading: () => {
        set({ globalLoading: initialGlobalLoading }, false, 'ui/clearGlobalLoading');
      },

      // ===== SPECIFIC LOADING OPERATIONS =====
      
      setLoading: (key: string, isLoading: boolean, message?: string, progress?: number) => {
        set(state => ({
          loadingStates: {
            ...state.loadingStates,
            [key]: {
              isLoading,
              loadingMessage: message,
              progress,
            }
          }
        }), false, 'ui/setLoading');
      },

      clearLoading: (key: string) => {
        set(state => {
          const { [key]: removed, ...remaining } = state.loadingStates;
          return { loadingStates: remaining };
        }, false, 'ui/clearLoading');
      },

      clearAllLoading: () => {
        set({ 
          globalLoading: initialGlobalLoading,
          loadingStates: {} 
        }, false, 'ui/clearAllLoading');
      },

      // ===== MODAL OPERATIONS =====
      
      openModal: (modalType: string, data?: any) => {
        set({
          modal: {
            isOpen: true,
            modalType,
            modalData: data,
          }
        }, false, 'ui/openModal');
      },

      closeModal: () => {
        set({ modal: initialModal }, false, 'ui/closeModal');
      },

      // ===== DRAWER OPERATIONS =====
      
      openDrawer: (drawerType: string, data?: any, height?: string) => {
        set({
          drawer: {
            isOpen: true,
            drawerType,
            drawerData: data,
            drawerHeight: height || '50%',
          }
        }, false, 'ui/openDrawer');
      },

      closeDrawer: () => {
        set({ drawer: initialDrawer }, false, 'ui/closeDrawer');
      },

      setDrawerHeight: (height: string) => {
        set(state => ({
          drawer: {
            ...state.drawer,
            drawerHeight: height,
          }
        }), false, 'ui/setDrawerHeight');
      },

      // ===== NOTIFICATION OPERATIONS =====
      
      addNotification: (notification: Omit<Notification, 'id' | 'timestamp'>) => {
        const newNotification: Notification = {
          ...notification,
          id: generateId(),
          timestamp: Date.now(),
        };

        set(state => {
          const notifications = [newNotification, ...state.notifications.notifications];
          const trimmedNotifications = notifications.slice(0, state.notifications.maxNotifications);
          
          return {
            notifications: {
              ...state.notifications,
              notifications: trimmedNotifications,
            }
          };
        }, false, 'ui/addNotification');

        // Auto-remove notification if duration is specified
        if (notification.duration && notification.duration > 0) {
          setTimeout(() => {
            get().removeNotification(newNotification.id);
          }, notification.duration);
        }
      },

      removeNotification: (id: string) => {
        set(state => ({
          notifications: {
            ...state.notifications,
            notifications: state.notifications.notifications.filter(n => n.id !== id),
          }
        }), false, 'ui/removeNotification');
      },

      clearNotifications: () => {
        set(state => ({
          notifications: {
            ...state.notifications,
            notifications: [],
          }
        }), false, 'ui/clearNotifications');
      },

      // ===== GENERAL UI OPERATIONS =====
      
      setInitialized: (isInitialized: boolean) => {
        set({ isInitialized }, false, 'ui/setInitialized');
      },

      setTheme: (theme: 'light' | 'dark' | 'auto') => {
        set({ theme }, false, 'ui/setTheme');
      },

      setMobile: (isMobile: boolean) => {
        set({ isMobile }, false, 'ui/setMobile');
      },

      setOnline: (isOnline: boolean) => {
        const wasOffline = !get().isOnline;
        set({ isOnline }, false, 'ui/setOnline');
        
        // Show notification when coming back online
        if (wasOffline && isOnline) {
          get().addNotification({
            type: 'success',
            title: 'Connection Restored',
            message: 'You are back online!',
            duration: 3000,
          });
        }
      },

      // ===== ERROR BOUNDARY OPERATIONS =====
      
      setGlobalError: (error: string | null) => {
        set({ 
          hasGlobalError: !!error,
          globalError: error 
        }, false, 'ui/setGlobalError');
      },

      clearGlobalError: () => {
        set({ 
          hasGlobalError: false,
          globalError: null 
        }, false, 'ui/clearGlobalError');
      },

      // ===== UTILITY FUNCTIONS =====
      
      isAnyLoading: () => {
        const { globalLoading, loadingStates } = get();
        return globalLoading.isLoading || Object.values(loadingStates).some(state => state.isLoading);
      },

      getLoadingKeys: () => {
        const { loadingStates } = get();
        return Object.keys(loadingStates).filter(key => loadingStates[key].isLoading);
      },
    }),
    {
      name: 'ui-store',
      enabled: process.env.NODE_ENV === 'development',
    }
  )
);

// ===== SELECTOR HOOKS =====

/**
 * Optimized selectors for common use cases
 * These prevent unnecessary re-renders by only subscribing to specific data
 */

// Only re-render when global loading state changes
export const useGlobalLoading = () => 
  useUIStore(state => state.globalLoading);

// Only re-render when specific loading state changes
export const useLoading = (key: string) => 
  useUIStore(state => state.loadingStates[key] || { isLoading: false });

// Only re-render when modal state changes
export const useModal = () => 
  useUIStore(state => state.modal);

// Only re-render when drawer state changes
export const useDrawer = () => 
  useUIStore(state => state.drawer);

// Only re-render when notifications change
export const useNotifications = () => 
  useUIStore(state => state.notifications.notifications);

// Only re-render when UI state changes
export const useUIState = () => 
  useUIStore(state => ({
    isInitialized: state.isInitialized,
    theme: state.theme,
    isMobile: state.isMobile,
    isOnline: state.isOnline,
  }));

// Only re-render when error state changes
export const useGlobalError = () => 
  useUIStore(state => ({
    hasError: state.hasGlobalError,
    error: state.globalError,
  }));

// Combined selector for any loading state
export const useAnyLoading = () => 
  useUIStore(state => state.isAnyLoading());

// ===== TYPE EXPORTS =====

export type { 
  UIState, 
  UIActions, 
  UIStore,
  LoadingState,
  ModalState,
  DrawerState,
  NotificationState,
  Notification
}; 